import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import { CalendarSyncPair } from 'services/selfSchedulingServices';
import ProfileMappingsTable from './ProfileMappingsTable';
import { ERROR_TEXT_COLOR, StatusChip, formatDate, retryText } from './syncStatusDisplay';

/**
 * MM-1168: the rebuild status cell - "In progress" while this page's sync request runs, else the
 * status stored in the DB. MM-1169: with the automatic retry state underneath.
 */
const StatusCell = ({ pair, syncing }: { pair: CalendarSyncPair; syncing: boolean }) => {
  const retry = syncing ? null : retryText(pair);
  return (
    <Box>
      <StatusChip status={syncing ? 'IN_PROGRESS' : pair.rebuildStatus} />
      {retry ? (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', whiteSpace: 'nowrap', mt: 0.5 }}>
          {retry}
        </Typography>
      ) : null}
    </Box>
  );
};

/**
 * MM-1169: the row's action - the Sync Calendar button only while the pair is not queued or being rebuilt (a
 * template save or another sync may have queued it); otherwise "Queued", or a spinner while it is the pair being
 * rebuilt, until it is completed or failed. The rebuild worker takes one location + provider at a time.
 */
const SyncAction = ({ pair, syncing, onSync }: { pair: CalendarSyncPair; syncing: boolean; onSync: () => void }) => {
  const queued = pair.rebuildStatus === 'PENDING';
  if (queued) {
    // waiting in the queue - not being processed yet, so no spinner
    return (
      <Typography variant='body2' color='text.secondary' sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>
        Queued
      </Typography>
    );
  }
  if (syncing || pair.rebuildStatus === 'IN_PROGRESS') {
    // the one pair the rebuild worker is processing right now
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 140, justifyContent: 'center' }}>
        <CircularProgress size={16} />
        <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
          Syncing...
        </Typography>
      </Box>
    );
  }
  return (
    <Button
      variant='contained'
      size='small'
      onClick={onSync}
      startIcon={<SyncIcon />}
      sx={{ textTransform: 'none', minWidth: 140, whiteSpace: 'nowrap' }}
    >
      Sync Calendar
    </Button>
  );
};

/**
 * MM-1168: one location + provider row of the Sync Calendar table; the arrow opens its profile
 * mappings with each one's details, status and error.
 * label - the provider's name
 * pair - the pair, its rebuild tracking and its profile mappings
 * columnCount - number of columns of the outer table (for the nested row's span)
 * expanded - whether the profile mappings are shown
 * onToggle - shows / hides the profile mappings
 * syncing - true while this page's sync request for the pair runs
 * onSync - starts the sync of this row's pair
 */
const SyncPairRow = ({
  label,
  pair,
  columnCount,
  expanded,
  onToggle,
  syncing,
  onSync,
}: {
  label: string;
  pair: CalendarSyncPair;
  columnCount: number;
  expanded: boolean;
  onToggle: () => void;
  syncing: boolean;
  onSync: () => void;
}) => (
  <>
    <TableRow hover sx={{ '& > td': { borderBottom: expanded ? 'none' : undefined } }}>
      <TableCell sx={{ pl: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton size='small' onClick={onToggle} aria-label={expanded ? 'Hide profiles' : 'Show profiles'}>
            {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
          <Typography variant='body2' sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {pair.profileCount || '-'}
          {pair.errorCount ? (
            <Chip size='small' color='error' variant='outlined' label={`${pair.errorCount} failed`} />
          ) : null}
        </Box>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(pair.profileUpdatedDate)}</TableCell>
      <TableCell>
        <StatusCell pair={pair} syncing={syncing} />
      </TableCell>
      <TableCell sx={{ maxWidth: 300 }}>
        {/* MM-1169: pre-line - one line per failed attempt */}
        <Typography
          variant='body2'
          sx={{ color: pair.rebuildMessage ? ERROR_TEXT_COLOR : 'text.secondary', whiteSpace: 'pre-line' }}
        >
          {pair.rebuildMessage ?? '-'}
        </Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(pair.rebuildStartedDate)}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(pair.lastCompletedDate)}</TableCell>
      <TableCell align='center'>
        <SyncAction pair={pair} syncing={syncing} onSync={onSync} />
      </TableCell>
    </TableRow>
    <TableRow>
      <TableCell colSpan={columnCount} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
        <Collapse in={expanded} timeout='auto' unmountOnExit>
          <ProfileMappingsTable mappings={pair.mappings ?? []} />
        </Collapse>
      </TableCell>
    </TableRow>
  </>
);

export default SyncPairRow;
